import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    ArrowLeft, Sparkle, Shield, WarningCircle, ChatCircle, ChartBar, ListChecks, ClockCounterClockwise, Play, Pause, XCircle, PencilSimple, ArrowClockwise, CheckCircle,
} from "@phosphor-icons/react";
import { agentsApi } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { PassportLogo } from "@/components/Reveal";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

const getTabs = (t) => [
    { k: "overview", l: t("ownerCenter.tabs.overview"), icon: ChartBar },
    { k: "agents", l: t("ownerCenter.tabs.agents"), icon: Sparkle },
    { k: "alerts", l: t("ownerCenter.tabs.alerts"), icon: WarningCircle },
    { k: "tickets", l: t("ownerCenter.tabs.tickets"), icon: ChatCircle },
    { k: "reports", l: t("ownerCenter.tabs.reports"), icon: ListChecks },
    { k: "audit", l: t("ownerCenter.tabs.audit"), icon: ClockCounterClockwise },
];

export default function OwnerCommandCenter() {
    const { t } = useTranslation();
    const tabs = useMemo(() => getTabs(t), [t]);
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const [tab, setTab] = useState("overview");
    const [overview, setOverview] = useState(null);
    const [agents, setAgents] = useState([]);
    const [alerts, setAlerts] = useState([]);
    const [tickets, setTickets] = useState([]);
    const [reports, setReports] = useState([]);
    const [audit, setAudit] = useState([]);
    const [selectedAgent, setSelectedAgent] = useState(null);
    const [gen, setGen] = useState(false);

    const refresh = async () => {
        try {
            const [o, a, al, ti, r, au] = await Promise.all([
                agentsApi.ownerOverview(),
                agentsApi.listAgents(),
                agentsApi.ownerAlerts({ open_only: false, limit: 100 }),
                agentsApi.ownerTickets({ limit: 40 }),
                agentsApi.listReports(),
                agentsApi.ownerAudit(),
            ]);
            setOverview(o); setAgents(a); setAlerts(al); setTickets(ti); setReports(r); setAudit(au);
        } catch (e) { toast.error(t("ownerCenter.toast.loadFailed")); }
    };

    useEffect(() => {
        if (loading) return;
        if (!user) { navigate("/auth"); return; }
        if (!(user.is_admin || ["admin", "owner"].includes(user.role))) {
            toast.error(t("ownerCenter.toast.ownerOnly"));
            navigate("/app");
            return;
        }
        refresh();
        const interval = setInterval(refresh, 20000);
        return () => clearInterval(interval);
        // eslint-disable-next-line
    }, [user, loading]);

    if (loading || !user) return <div className="min-h-screen grid place-items-center text-white/60">{t("common.loading")}</div>;

    const manager = agents.find((a) => a.role === "manager");
    const specialists = agents.filter((a) => a.role === "specialist");
    const openAlerts = alerts.filter((a) => !a.resolved);
    const criticalAlerts = openAlerts.filter((a) => a.severity === "critical");

    return (
        <div className="min-h-screen grain" data-testid="owner-center">
            <header className="sticky top-0 z-40 glass border-b" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
                <div className="max-w-[1300px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between gap-3">
                    <Link to="/app" className="flex items-center gap-3" data-testid="owner-back">
                        <ArrowLeft size={16} color="#D4AF37" />
                        <PassportLogo size={26} />
                        <div className="hidden sm:block leading-none">
                            <div className="font-serif text-base text-white">{t("ownerCenter.headerTitle")}</div>
                            <div className="text-[9px] tracking-[0.3em] uppercase" style={{ color: "var(--gold)" }}>{t("ownerCenter.headerSub")}</div>
                        </div>
                    </Link>
                    <div className="flex items-center gap-2">
                        <Link to="/admin/local-events" className="btn-ghost text-xs !py-2 !px-3" data-testid="owner-events-admin-link">
                            {t("ownerCenter.localEvents")}
                        </Link>
                        <button onClick={refresh} className="btn-ghost text-xs !py-2 !px-3" data-testid="owner-refresh">
                            <ArrowClockwise size={12} /> {t("admin.refresh")}
                        </button>
                        <button onClick={async () => { try { await agentsApi.pauseAll(); toast.success(t("ownerCenter.toast.allAiPaused")); refresh(); } catch (e) { toast.error(e?.response?.data?.detail || t("ownerCenter.toast.pauseAllFailed")); } }} className="btn-ghost text-xs !py-2 !px-3" data-testid="owner-pause-all">
                            <Pause size={12} /> {t("ownerCenter.pauseAll")}
                        </button>
                        <button onClick={async () => { try { await agentsApi.resumeAll(); toast.success(t("ownerCenter.toast.allAiResumed")); refresh(); } catch (e) { toast.error(e?.response?.data?.detail || t("ownerCenter.toast.resumeAllFailed")); } }} className="btn-gold text-xs !py-2 !px-4" data-testid="owner-resume-all">
                            <Play size={12} weight="fill" /> {t("ownerCenter.resumeAll")}
                        </button>
                    </div>
                </div>
            </header>

            <div className="max-w-[1300px] mx-auto px-6 md:px-10 py-8">
                <div className="overline mb-2" style={{ color: "var(--gold)" }}>{t("ownerCenter.overline")}</div>
                <h1 className="font-serif text-4xl md:text-5xl text-white leading-tight tracking-tight">{t("ownerCenter.title")}</h1>
                <p className="text-white/60 mt-3">
                    {t("ownerCenter.subtitle", { count: specialists.length })}
                    {criticalAlerts.length > 0 && <span className="ml-3 text-[#FF8888]">{t("ownerCenter.criticalAlerts", { count: criticalAlerts.length })}</span>}
                </p>

                <div className="mt-8 flex flex-wrap gap-2" data-testid="owner-tabs">
                    {tabs.map((tItem) => {
                        const Icon = tItem.icon;
                        const badge = tItem.k === "alerts" ? openAlerts.length : tItem.k === "reports" ? reports.filter((r) => r.approved === null).length : 0;
                        return (
                            <button
                                key={tItem.k}
                                onClick={() => setTab(tItem.k)}
                                className="px-4 py-2 rounded-full text-xs transition-all inline-flex items-center gap-2"
                                style={{
                                    background: tab === tItem.k ? "rgba(212,175,55,0.15)" : "rgba(255,255,255,0.04)",
                                    border: `1px solid ${tab === tItem.k ? "rgba(212,175,55,0.5)" : "rgba(255,255,255,0.08)"}`,
                                    color: tab === tItem.k ? "var(--gold)" : "rgba(255,255,255,0.75)",
                                }}
                                data-testid={`owner-tab-${tItem.k}`}
                            >
                                <Icon size={11} /> {tItem.l}
                                {badge > 0 && <span className="text-[10px] px-1.5 rounded-full" style={{ background: "rgba(255,120,120,0.2)", color: "#FF8888" }}>{badge}</span>}
                            </button>
                        );
                    })}
                </div>

                <div className="mt-8">
                    {tab === "overview" && <Overview overview={overview} manager={manager} specialists={specialists} onSelectAgent={setSelectedAgent} t={t} />}
                    {tab === "agents" && <AgentsGrid manager={manager} specialists={specialists} onSelect={setSelectedAgent} t={t} />}
                    {tab === "alerts" && <AlertsPanel alerts={alerts} onAck={async (id) => { try { await agentsApi.ackAlert(id, { resolved: true }); refresh(); } catch (e) { toast.error(e?.response?.data?.detail || t("ownerCenter.toast.ackFailed")); } }} t={t} />}
                    {tab === "tickets" && <TicketsPanel tickets={tickets} t={t} />}
                    {tab === "reports" && <ReportsPanel reports={reports} gen={gen} onGenerate={async () => { setGen(true); try { await agentsApi.generateReport(); toast.success(t("ownerCenter.toast.reportGenerated")); refresh(); } catch (e) { toast.error(e?.response?.data?.detail || t("ownerCenter.toast.couldNotGenerate")); } finally { setGen(false); } }} onDecide={async (id, approved) => { try { await agentsApi.decideReport(id, { approved }); toast.success(approved ? t("ownerCenter.toast.approved") : t("ownerCenter.toast.rejected")); refresh(); } catch (e) { toast.error(e?.response?.data?.detail || t("ownerCenter.toast.decisionFailed")); } }} t={t} />}
                    {tab === "audit" && <AuditPanel audit={audit} t={t} />}
                </div>
            </div>

            <AnimatePresence>
                {selectedAgent && <AgentDetail agent={selectedAgent} onClose={() => setSelectedAgent(null)} onChange={(opts) => { if (!opts?.silent) setSelectedAgent(null); refresh(); }} t={t} />}
            </AnimatePresence>
        </div>
    );
}

/* ===== Sub-views ===== */
function Overview({ overview, manager, specialists, onSelectAgent, t }) {
    if (!overview) return <div className="text-white/40">{t("common.loading")}</div>;
    return (
        <div className="space-y-8">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <StatCard label={t("ownerCenter.stats.tickets24h")} value={overview.tickets_24h} />
                <StatCard label={t("ownerCenter.stats.resolved24h")} value={`${overview.resolved_24h}`} sub={t("ownerCenter.stats.rate", { rate: overview.resolution_rate_24h })} />
                <StatCard label={t("ownerCenter.stats.escalated24h")} value={overview.escalated_24h} tone={overview.escalated_24h > 0 ? "warn" : "ok"} />
                <StatCard label={t("ownerCenter.stats.openAlerts")} value={overview.alerts_open} sub={t("ownerCenter.stats.criticalCount", { count: overview.alerts_critical })} tone={overview.alerts_critical > 0 ? "crit" : overview.alerts_open > 0 ? "warn" : "ok"} />
            </div>

            <OrgChart manager={manager} specialists={specialists} onSelect={onSelectAgent} t={t} />

            <div>
                <div className="overline mb-3">{t("ownerCenter.topIssuesOverline")}</div>
                {overview.top_issues_7d?.length ? (
                    <div className="flex flex-wrap gap-2">
                        {overview.top_issues_7d.map((i, k) => (
                            <span key={k} className="text-[12px] px-3 py-1.5 rounded-full" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(212,175,55,0.25)", color: "rgba(255,255,255,0.85)" }}>
                                {i.intent} · <span style={{ color: "var(--gold)" }}>{i.count}</span>
                            </span>
                        ))}
                    </div>
                ) : (
                    <div className="text-white/40 text-sm">{t("ownerCenter.noTickets7d")}</div>
                )}
            </div>
        </div>
    );
}

function StatCard({ label, value, sub, tone = "ok" }) {
    const tint = tone === "crit" ? "#FF8888" : tone === "warn" ? "#F4A28F" : "var(--gold)";
    return (
        <div className="rounded-3xl p-5" style={{ background: "var(--navy-deep)", border: "1px solid rgba(255,255,255,0.06)" }}>
            <div className="text-[10px] tracking-[0.25em] uppercase text-white/50">{label}</div>
            <div className="font-serif text-4xl mt-2" style={{ color: tint }}>{value}</div>
            {sub && <div className="text-[11px] text-white/50 mt-1">{sub}</div>}
        </div>
    );
}

function OrgChart({ manager, specialists, onSelect, t }) {
    return (
        <div className="rounded-3xl p-6 md:p-8" style={{ background: "var(--navy-deep)", border: "1px solid rgba(212,175,55,0.15)" }} data-testid="org-chart">
            <div className="overline mb-4">{t("ownerCenter.orgChartOverline")}</div>
            <div className="flex flex-col items-center gap-6">
                <NodePill icon="👑" title={t("ownerCenter.youOwner")} sub={t("ownerCenter.finalAuthority")} tone="owner" />
                <ChartLine />
                {manager && (
                    <div onClick={() => onSelect(manager)} className="cursor-pointer">
                        <NodePill icon={manager.icon} title={manager.name} sub={manager.id} tone="manager" status={manager.status} />
                    </div>
                )}
                <ChartLine />
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3 w-full">
                    {specialists.map((s) => (
                        <div key={s.id} onClick={() => onSelect(s)} className="cursor-pointer transition-transform hover:-translate-y-1" data-testid={`org-agent-${s.code}`}>
                            <NodePill icon={s.icon} title={s.name} sub={s.code} status={s.status} compact />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function ChartLine() {
    return <div className="w-px h-6" style={{ background: "linear-gradient(180deg, rgba(212,175,55,0.5), rgba(212,175,55,0.1))" }} />;
}

function NodePill({ icon, title, sub, tone, status, compact }) {
    const bg = tone === "owner" ? "rgba(212,175,55,0.16)" : tone === "manager" ? "rgba(212,175,55,0.1)" : "rgba(255,255,255,0.04)";
    const border = tone === "owner" ? "rgba(212,175,55,0.5)" : tone === "manager" ? "rgba(212,175,55,0.4)" : "rgba(255,255,255,0.08)";
    return (
        <div className="rounded-2xl px-4 py-3 flex items-center gap-3 min-w-[180px]" style={{ background: bg, border: `1px solid ${border}` }}>
            <div className="text-xl">{icon}</div>
            <div className="min-w-0">
                <div className={`${compact ? "text-[12px]" : "text-sm"} text-white font-medium leading-tight line-clamp-1`}>{title}</div>
                <div className="text-[9px] tracking-[0.15em] uppercase text-white/50 line-clamp-1 flex items-center gap-1.5">
                    {sub}
                    {status && status !== "active" && <span className="px-1.5 py-0.5 rounded" style={{ background: status === "paused" ? "rgba(244,162,143,0.15)" : "rgba(255,120,120,0.15)", color: status === "paused" ? "#F4A28F" : "#FF8888" }}>{status}</span>}
                </div>
            </div>
        </div>
    );
}

function AgentsGrid({ manager, specialists, onSelect, t }) {
    return (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[manager, ...specialists].filter(Boolean).map((a) => (
                <button
                    key={a.id}
                    onClick={() => onSelect(a)}
                    className="rounded-3xl p-5 text-left transition-transform hover:-translate-y-1"
                    style={{ background: "var(--navy-deep)", border: `1px solid ${a.status === "active" ? "rgba(212,175,55,0.25)" : a.status === "paused" ? "rgba(244,162,143,0.35)" : "rgba(255,120,120,0.35)"}` }}
                    data-testid={`agent-card-${a.code}`}
                >
                    <div className="flex items-start justify-between mb-3">
                        <div className="text-2xl">{a.icon}</div>
                        <StatusPill status={a.status} />
                    </div>
                    <div className="font-serif text-lg text-white leading-tight">{a.name}</div>
                    <div className="text-[10px] tracking-[0.15em] uppercase text-white/40 mt-1">{a.id}</div>
                    <p className="text-white/65 text-[12px] mt-3 line-clamp-3">{a.mission}</p>
                    {a.metrics && (
                        <div className="mt-4 flex flex-wrap gap-3 text-[11px] text-white/60">
                            <span><b style={{ color: "var(--gold)" }}>{a.metrics.tasks_total}</b> {t("ownerCenter.agentMetrics.tasks", { count: "" }).trim()}</span>
                            <span><b style={{ color: "#9FD5D0" }}>{a.metrics.avg_confidence}%</b> conf.</span>
                            <span><b style={{ color: "#F4A28F" }}>{a.metrics.escalation_rate}%</b> esc.</span>
                        </div>
                    )}
                </button>
            ))}
        </div>
    );
}

function StatusPill({ status }) {
    const tone = status === "paused" ? { bg: "rgba(244,162,143,0.14)", fg: "#F4A28F", b: "rgba(244,162,143,0.4)" }
        : status === "disabled" ? { bg: "rgba(255,120,120,0.14)", fg: "#FF8888", b: "rgba(255,120,120,0.4)" }
        : { bg: "rgba(159,213,208,0.12)", fg: "#9FD5D0", b: "rgba(159,213,208,0.35)" };
    return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[9px] uppercase tracking-[0.2em]" style={{ background: tone.bg, color: tone.fg, border: `1px solid ${tone.b}` }}>
            {status || "active"}
        </span>
    );
}

function AlertsPanel({ alerts, onAck, t }) {
    if (!alerts.length) return <div className="text-white/50">{t("ownerCenter.noAlerts")}</div>;
    return (
        <div className="space-y-2">
            {alerts.map((a) => (
                <div key={a.id} className="rounded-2xl p-4 flex items-start gap-3" style={{ background: "var(--navy-deep)", border: `1px solid ${a.severity === "critical" ? "rgba(255,120,120,0.4)" : a.severity === "warning" ? "rgba(244,162,143,0.4)" : "rgba(255,255,255,0.08)"}` }} data-testid={`alert-${a.id}`}>
                    <div className="mt-0.5">
                        {a.severity === "critical" ? <WarningCircle size={18} weight="fill" color="#FF8888" /> : a.severity === "warning" ? <WarningCircle size={18} color="#F4A28F" /> : <WarningCircle size={16} color="rgba(255,255,255,0.4)" />}
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-[10px] tracking-[0.2em] uppercase text-white/50 mb-0.5">{a.severity} · {a.type} · {timeAgo(a.created_at)}</div>
                        <div className="text-white/90 text-sm">{a.message}</div>
                        {a.resolved && <div className="text-[11px] text-white/40 mt-1">{t("ownerCenter.resolvedBy", { by: a.resolved_by, res: a.resolution })}</div>}
                    </div>
                    {!a.resolved && (
                        <button onClick={() => onAck(a.id)} className="text-[11px] uppercase tracking-[0.15em]" style={{ color: "var(--gold)" }} data-testid={`alert-ack-${a.id}`}>
                            <CheckCircle size={12} weight="fill" className="inline mr-1" /> {t("ownerCenter.ack")}
                        </button>
                    )}
                </div>
            ))}
        </div>
    );
}

function TicketsPanel({ tickets, t }) {
    if (!tickets.length) return <div className="text-white/50">{t("ownerCenter.noTickets")}</div>;
    return (
        <div className="space-y-2">
            {tickets.map((tItem) => (
                <div key={tItem.id} className="rounded-2xl p-4" style={{ background: "var(--navy-deep)", border: "1px solid rgba(255,255,255,0.06)" }} data-testid={`ticket-${tItem.id}`}>
                    <div className="flex items-center gap-2 mb-1 text-[11px] uppercase tracking-[0.15em] text-white/50">
                        <span>#{tItem.id.slice(0, 8)}</span>
                        <span>·</span>
                        <span>{tItem.user_name}</span>
                        <span>·</span>
                        <span>{tItem.status}</span>
                        {tItem.escalated && <span className="px-2 py-0.5 rounded-full" style={{ background: "rgba(255,120,120,0.15)", color: "#FF8888" }}>{t("ownerCenter.escalated")}</span>}
                        {tItem.intent && <span style={{ color: "var(--gold)" }}>· {tItem.intent}</span>}
                    </div>
                    <div className="text-white text-sm mb-2 line-clamp-1">{tItem.subject}</div>
                    {tItem.messages && tItem.messages.map((m, k) => (
                        <div key={k} className="text-[12px] text-white/70 mt-1 pl-2 border-l" style={{ borderColor: m.role === "user" ? "rgba(255,255,255,0.1)" : "rgba(212,175,55,0.35)" }}>
                            <span className="text-[10px] uppercase tracking-[0.15em] mr-2" style={{ color: m.role === "user" ? "rgba(255,255,255,0.4)" : "var(--gold)" }}>{m.role}</span>
                            <span className="line-clamp-2">{m.text}</span>
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
}

function ReportsPanel({ reports, gen, onGenerate, onDecide, t }) {
    return (
        <div className="space-y-4">
            <button onClick={onGenerate} disabled={gen} className="btn-gold text-sm !py-2.5 !px-5 disabled:opacity-50" data-testid="owner-generate-report">
                <Sparkle size={14} weight="fill" /> {gen ? t("ownerCenter.generating") : t("ownerCenter.generateDailyReport")}
            </button>
            {reports.length === 0 && <div className="text-white/40">{t("ownerCenter.noReportsYet")}</div>}
            {reports.map((r) => (
                <div key={r.id} className="rounded-3xl p-5" style={{ background: "var(--navy-deep)", border: "1px solid rgba(212,175,55,0.2)" }} data-testid={`report-${r.id}`}>
                    <div className="flex items-center justify-between mb-2">
                        <div className="overline">{t("ownerCenter.dailyReportOverline", { date: new Date(r.created_at).toLocaleString() })}</div>
                        {r.approved === null ? (
                            <div className="flex gap-1">
                                <button onClick={() => onDecide(r.id, true)} className="text-[11px] px-3 py-1.5 rounded-full" style={{ background: "rgba(159,213,208,0.14)", color: "#9FD5D0", border: "1px solid rgba(159,213,208,0.35)" }} data-testid={`report-approve-${r.id}`}>{t("ownerCenter.approve")}</button>
                                <button onClick={() => onDecide(r.id, false)} className="text-[11px] px-3 py-1.5 rounded-full" style={{ background: "rgba(255,120,120,0.14)", color: "#FF8888", border: "1px solid rgba(255,120,120,0.35)" }} data-testid={`report-reject-${r.id}`}>{t("ownerCenter.reject")}</button>
                            </div>
                        ) : (
                            <span className="text-[10px] uppercase tracking-[0.2em]" style={{ color: r.approved ? "#9FD5D0" : "#FF8888" }}>{r.approved ? t("ownerCenter.toast.approved") : t("ownerCenter.toast.rejected")}</span>
                        )}
                    </div>
                    <div className="font-serif text-xl text-white leading-tight mb-3">{r.summary_line}</div>
                    <ReportSection label={t("ownerCenter.reportSections.customerSupport")} text={r.customer_support} />
                    <ReportSection label={t("ownerCenter.reportSections.technical")} text={r.technical} />
                    <ReportSection label={t("ownerCenter.reportSections.customerFeedback")} text={r.customer_feedback} />
                    {r.top_issues?.length > 0 && (
                        <div className="mt-3">
                            <div className="text-[10px] tracking-[0.2em] uppercase text-white/50 mb-1">{t("ownerCenter.topIssues")}</div>
                            <ul className="text-[13px] text-white/80 space-y-1">
                                {r.top_issues.map((i, k) => <li key={k}>· {typeof i === "string" ? i : JSON.stringify(i)}</li>)}
                            </ul>
                        </div>
                    )}
                    {r.recommended_actions?.length > 0 && (
                        <div className="mt-3">
                            <div className="text-[10px] tracking-[0.2em] uppercase mb-1" style={{ color: "var(--gold)" }}>{t("ownerCenter.recommendedActions")}</div>
                            <ul className="text-[13px] text-white/80 space-y-1">
                                {r.recommended_actions.map((i, k) => <li key={k}>› {typeof i === "string" ? i : JSON.stringify(i)}</li>)}
                            </ul>
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
}

function ReportSection({ label, text }) {
    if (!text) return null;
    return (
        <div className="mt-3">
            <div className="text-[10px] tracking-[0.2em] uppercase text-white/50 mb-1">{label}</div>
            <div className="text-[13px] text-white/80 whitespace-pre-wrap">{text}</div>
        </div>
    );
}

function AuditPanel({ audit, t }) {
    if (!audit.length) return <div className="text-white/50">{t("ownerCenter.noAuditEntries")}</div>;
    return (
        <div className="space-y-1.5">
            {audit.map((a) => (
                <div key={a.id} className="flex items-start gap-3 text-[12px] rounded-lg px-3 py-2" style={{ background: "rgba(255,255,255,0.03)" }} data-testid={`audit-${a.id}`}>
                    <span className="text-white/40 shrink-0 min-w-[100px]">{new Date(a.created_at).toLocaleString()}</span>
                    <span className="text-white/70 shrink-0 min-w-[90px]">{a.actor.slice(0, 10)}</span>
                    <span style={{ color: "var(--gold)" }} className="shrink-0 min-w-[110px]">{a.action}</span>
                    <span className="text-white/60 truncate">{a.target}</span>
                </div>
            ))}
        </div>
    );
}

function AgentDetail({ agent, onClose, onChange, t }) {
    const [detail, setDetail] = useState(agent);
    const [editing, setEditing] = useState(false);
    const [prompt, setPrompt] = useState(agent.system_prompt || "");
    const [busy, setBusy] = useState(false);
    useEffect(() => {
        agentsApi.getAgent(agent.id).then((d) => { setDetail(d); setPrompt(d.system_prompt || ""); }).catch(() => {});
    }, [agent.id]);

    const refreshDetail = async () => {
        try {
            const d = await agentsApi.getAgent(agent.id);
            setDetail(d);
            setPrompt(d.system_prompt || "");
        } catch (_) {}
    };

    const savePrompt = async () => {
        setBusy(true);
        try {
            await agentsApi.patchAgent(agent.id, { system_prompt: prompt });
            toast.success(t("ownerCenter.toast.instructionsUpdated"));
            setEditing(false);
            await refreshDetail();
            onChange({ silent: true });
        } catch (e) { toast.error(e?.response?.data?.detail || t("ownerCenter.toast.updateFailed")); }
        finally { setBusy(false); }
    };

    const setStatus = async (fn, label) => {
        setBusy(true);
        try {
            await fn(agent.id);
            toast.success(t("ownerCenter.toast.agentStatus", { label }));
            await refreshDetail();
            onChange({ silent: true });
        } catch (e) { toast.error(e?.response?.data?.detail || t("ownerCenter.toast.failed")); }
        finally { setBusy(false); }
    };

    return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-[80] flex items-start justify-center p-4 overflow-y-auto"
            style={{ background: "rgba(5,10,17,0.85)", backdropFilter: "blur(6px)" }}
            data-testid="agent-detail"
        >
            <motion.div onClick={(e) => e.stopPropagation()} initial={{ y: 24 }} animate={{ y: 0 }} className="rounded-3xl max-w-2xl w-full p-6 md:p-8 my-8" style={{ background: "var(--navy)", border: "1px solid rgba(212,175,55,0.3)" }}>
                <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                        <div className="text-3xl">{detail.icon}</div>
                        <div>
                            <div className="font-serif text-2xl text-white leading-tight">{detail.name}</div>
                            <div className="text-[10px] tracking-[0.25em] uppercase" style={{ color: "var(--gold)" }}>{detail.id} · v{detail.version}</div>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-white/60 hover:text-white" aria-label="Close">
                        <XCircle size={20} />
                    </button>
                </div>

                <p className="text-white/70 text-sm mb-3">{detail.mission}</p>

                <div className="flex flex-wrap gap-2 mb-4">
                    <StatusPill status={detail.status} />
                    {(detail.permissions || []).map((p) => (
                        <span key={p} className="text-[10px] px-2 py-0.5 rounded-full uppercase tracking-[0.2em]" style={{ background: "rgba(159,213,208,0.1)", color: "#9FD5D0", border: "1px solid rgba(159,213,208,0.3)" }}>{p}</span>
                    ))}
                </div>

                {detail.metrics && (
                    <div className="grid grid-cols-4 gap-3 mb-6">
                        <MiniStat label={t("ownerCenter.miniStats.tasks")} value={detail.metrics.tasks_total} />
                        <MiniStat label={t("ownerCenter.miniStats.escalated")} value={detail.metrics.tasks_escalated} />
                        <MiniStat label={t("ownerCenter.miniStats.escRate")} value={`${detail.metrics.escalation_rate}%`} />
                        <MiniStat label={t("ownerCenter.miniStats.avgConf")} value={`${detail.metrics.avg_confidence}%`} />
                    </div>
                )}

                <div className="mb-6">
                    <div className="flex items-center justify-between mb-2">
                        <div className="text-[10px] tracking-[0.25em] uppercase text-white/50">{t("ownerCenter.agentDetail.systemInstructions")}</div>
                        {!editing && detail.id !== "PSP-AI-MGR-001" && (
                            <button onClick={() => setEditing(true)} className="text-[11px]" style={{ color: "var(--gold)" }} data-testid="agent-edit">
                                <PencilSimple size={11} className="inline mr-1" /> {t("common.edit")}
                            </button>
                        )}
                    </div>
                    {editing ? (
                        <>
                            <textarea
                                value={prompt}
                                onChange={(e) => setPrompt(e.target.value)}
                                rows={8}
                                className="w-full text-[12px] p-3 rounded-2xl text-white font-mono"
                                style={{ background: "rgba(0,0,0,0.4)", border: "1px solid rgba(212,175,55,0.3)" }}
                                data-testid="agent-prompt-input"
                            />
                            <div className="flex gap-2 mt-2">
                                <button onClick={savePrompt} disabled={busy} className="btn-gold text-xs !py-2 !px-4" data-testid="agent-save">{t("common.save")}</button>
                                <button onClick={() => { setEditing(false); setPrompt(detail.system_prompt || ""); }} className="btn-ghost text-xs !py-2 !px-4">{t("common.cancel")}</button>
                            </div>
                        </>
                    ) : (
                        <div className="text-[12px] text-white/80 whitespace-pre-wrap rounded-2xl p-3" style={{ background: "rgba(0,0,0,0.25)" }}>{detail.system_prompt}</div>
                    )}
                </div>

                <div className="flex flex-wrap gap-2">
                    {detail.status !== "active" && (
                        <button onClick={() => setStatus(agentsApi.activateAgent, "activated")} disabled={busy} className="text-[11px] px-4 py-2 rounded-full" style={{ background: "rgba(159,213,208,0.14)", color: "#9FD5D0", border: "1px solid rgba(159,213,208,0.3)" }} data-testid="agent-activate">
                            <Play size={11} weight="fill" className="inline mr-1" /> {t("ownerCenter.agentDetail.activate")}
                        </button>
                    )}
                    {detail.status !== "paused" && (
                        <button onClick={() => setStatus(agentsApi.pauseAgent, "paused")} disabled={busy} className="text-[11px] px-4 py-2 rounded-full" style={{ background: "rgba(244,162,143,0.14)", color: "#F4A28F", border: "1px solid rgba(244,162,143,0.3)" }} data-testid="agent-pause">
                            <Pause size={11} className="inline mr-1" /> {t("ownerCenter.agentDetail.pause")}
                        </button>
                    )}
                    {detail.status !== "disabled" && detail.id !== "PSP-AI-MGR-001" && (
                        <button onClick={() => setStatus(agentsApi.disableAgent, "disabled")} disabled={busy} className="text-[11px] px-4 py-2 rounded-full" style={{ background: "rgba(255,120,120,0.14)", color: "#FF8888", border: "1px solid rgba(255,120,120,0.3)" }} data-testid="agent-disable">
                            <XCircle size={11} className="inline mr-1" /> {t("ownerCenter.agentDetail.disable")}
                        </button>
                    )}
                </div>

                {detail.recent_tasks && detail.recent_tasks.length > 0 && (
                    <div className="mt-6">
                        <div className="text-[10px] tracking-[0.25em] uppercase text-white/50 mb-2">{t("ownerCenter.agentDetail.recentTasks")}</div>
                        <div className="space-y-2 max-h-[240px] overflow-y-auto">
                            {detail.recent_tasks.map((tItem) => (
                                <div key={tItem.id} className="text-[12px] p-3 rounded-2xl" style={{ background: "rgba(0,0,0,0.25)" }}>
                                    <div className="text-white/50 text-[10px] uppercase tracking-[0.15em] mb-1">conf {tItem.confidence}% · {tItem.escalation_required ? "escalated" : "closed"}</div>
                                    <div className="text-white/85 line-clamp-2">{tItem.instruction}</div>
                                    <div className="text-white/60 line-clamp-2 mt-1">→ {tItem.recommended_action}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </motion.div>
        </motion.div>
    );
}

function MiniStat({ label, value }) {
    return (
        <div className="rounded-2xl p-3" style={{ background: "rgba(0,0,0,0.25)" }}>
            <div className="text-[9px] tracking-[0.2em] uppercase text-white/50">{label}</div>
            <div className="font-serif text-xl text-white mt-1">{value}</div>
        </div>
    );
}

function timeAgo(iso) {
    if (!iso) return "";
    const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
}
